import React from 'react';
import { CreditCard, Shield } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { RadioGroup, RadioGroupItem } from '../ui/radio-group';
import { useCurrency } from '../CurrencySelector';

export const PaymentStep = ({ paymentMethod, onPaymentMethodChange, loading, amountDue, onPlaceOrder, language }) => {
  const { format, code } = useCurrency();
  return (
  <div className="bg-white rounded-xl border border-border/40 p-6" data-testid="payment-form">
    <h2 className="text-xl font-bold mb-6 flex items-center gap-2">
      <CreditCard className="w-5 h-5 text-[#0F4C5C]" />
      {language === 'fr' ? 'Méthode de paiement' : 'Payment Method'}
    </h2>

    <RadioGroup value={paymentMethod} onValueChange={onPaymentMethodChange} className="space-y-3 mb-6">
      <div
        className={`flex items-center justify-between p-4 border rounded-lg cursor-pointer transition-colors ${
          paymentMethod === 'card' ? 'border-[#0F4C5C] bg-[#0F4C5C]/5' : 'border-border hover:border-[#0F4C5C]/50'
        }`}
        onClick={() => onPaymentMethodChange('card')}
      >
        <div className="flex items-center gap-3">
          <RadioGroupItem value="card" id="card" />
          <Label htmlFor="card" className="cursor-pointer font-medium">
            {language === 'fr' ? 'Carte bancaire' : 'Credit/Debit Card'}
          </Label>
        </div>
        <div className="flex gap-2">
          <img src="https://upload.wikimedia.org/wikipedia/commons/thumb/5/5e/Visa_Inc._logo.svg/200px-Visa_Inc._logo.svg.png" alt="Visa" className="h-6" />
          <img src="https://upload.wikimedia.org/wikipedia/commons/thumb/2/2a/Mastercard-logo.svg/200px-Mastercard-logo.svg.png" alt="Mastercard" className="h-6" />
        </div>
      </div>

      <div
        className={`flex items-center justify-between p-4 border rounded-lg cursor-pointer transition-colors ${
          paymentMethod === 'paypal' ? 'border-[#0F4C5C] bg-[#0F4C5C]/5' : 'border-border hover:border-[#0F4C5C]/50'
        }`}
        onClick={() => onPaymentMethodChange('paypal')}
      >
        <div className="flex items-center gap-3">
          <RadioGroupItem value="paypal" id="paypal" />
          <Label htmlFor="paypal" className="cursor-pointer font-medium">PayPal</Label>
        </div>
        <img src="https://upload.wikimedia.org/wikipedia/commons/thumb/b/b5/PayPal.svg/200px-PayPal.svg.png" alt="PayPal" className="h-6" />
      </div>
    </RadioGroup>

    {paymentMethod === 'card' && (
      <div className="bg-slate-50 rounded-lg p-4 mb-6">
        <p className="text-sm text-muted-foreground mb-4">
          {language === 'fr'
            ? 'Mode démo : Aucun paiement réel ne sera effectué.'
            : 'Demo mode: No real payment will be processed.'}
        </p>
        <div className="space-y-4">
          <div>
            <Label>{language === 'fr' ? 'Numéro de carte' : 'Card Number'}</Label>
            <Input placeholder="4242 4242 4242 4242" className="mt-1" disabled />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>{language === 'fr' ? 'Date d\'expiration' : 'Expiry Date'}</Label>
              <Input placeholder="MM/YY" className="mt-1" disabled />
            </div>
            <div>
              <Label>CVC</Label>
              <Input placeholder="123" className="mt-1" disabled />
            </div>
          </div>
        </div>
      </div>
    )}

    <div className="flex items-center gap-3 p-4 bg-green-50 rounded-lg mb-6">
      <Shield className="w-6 h-6 text-green-600" />
      <p className="text-sm text-green-800">
        {language === 'fr'
          ? 'Vos données de paiement sont sécurisées et cryptées.'
          : 'Your payment data is secure and encrypted.'}
      </p>
    </div>

    <Button
      onClick={onPlaceOrder}
      className="w-full btn-primary h-14 text-lg"
      disabled={loading}
      data-testid="place-order-btn"
    >
      {loading
        ? (language === 'fr' ? 'Traitement...' : 'Processing...')
        : (language === 'fr' ? `Payer €${(amountDue || 0).toFixed(2)}` : `Pay €${(amountDue || 0).toFixed(2)}`)}
    </Button>
    {code !== 'EUR' && (
      <p className="text-xs text-muted-foreground text-center mt-2" data-testid="billed-in-eur-note">
        ≈ {format(amountDue)} · {language === 'fr' ? 'Le paiement est débité en euros (EUR)' : 'Payment is charged in euros (EUR)'}
      </p>
    )}
  </div>
  );
};
